export type CurrencyAmountInput = number | string | null | undefined;

const DEFAULT_LOCALE = "en-US";
const DEFAULT_CURRENCY = "USD";

const isCurrencyCode = (value: string): boolean => /^[A-Z]{3}$/.test(value);

/**
 * @description 标准化货币代码（ISO 4217），失败则返回 undefined
 * @example normalizeCurrencyCode(" usd ") => "USD"
 */
export function normalizeCurrencyCode(
  currency?: string | null,
): string | undefined {
  if (!currency) {
    return undefined;
  }
  const normalized = currency.trim().toUpperCase();
  return isCurrencyCode(normalized) ? normalized : undefined;
}

/**
 * @description 获取默认 locale（优先使用浏览器语言）
 */
export function getDefaultLocale(): string {
  if (
    typeof navigator !== "undefined" &&
    typeof navigator.language === "string"
  ) {
    return navigator.language || DEFAULT_LOCALE;
  }
  return DEFAULT_LOCALE;
}

/**
 * @description 通过 Intl.NumberFormat 获取货币符号（narrow symbol 优先）
 *
 * - **优点**：不需要维护映射表，跟随系统/浏览器 ICU 数据
 * - **兜底**：currency 非法或运行环境不支持时，回退到 "$"
 */
export function getCurrencySymbolFromCode(
  currencyCode?: string | null,
  locale?: string,
): string {
  const normalizedCurrency =
    normalizeCurrencyCode(currencyCode) || DEFAULT_CURRENCY;
  const resolvedLocale = locale || getDefaultLocale();

  try {
    const parts = new Intl.NumberFormat(resolvedLocale, {
      style: "currency",
      currency: normalizedCurrency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);

    return parts.find((p) => p.type === "currency")?.value || "$";
  } catch {
    return "$";
  }
}

/**
 * @description 格式化金额为本地化货币字符串
 *
 * - amount 支持 number / string（string 会尝试转换为 number）
 * - currencyCode 若无/非法：默认 USD（保持历史行为与 UI 一致）
 * - locale 若无：默认使用浏览器语言
 */
export function formatCurrencyAmount(
  amount: CurrencyAmountInput,
  options?: {
    currencyCode?: string | null;
    locale?: string;
  },
): string {
  if (amount === null || amount === undefined || amount === "") {
    return "--";
  }

  const value =
    typeof amount === "number" ? amount : Number.parseFloat(String(amount));
  if (!Number.isFinite(value)) {
    return "--";
  }

  const resolvedLocale = options?.locale || getDefaultLocale();
  const normalizedCurrency =
    normalizeCurrencyCode(options?.currencyCode) || DEFAULT_CURRENCY;

  try {
    return new Intl.NumberFormat(resolvedLocale, {
      style: "currency",
      currency: normalizedCurrency,
      currencyDisplay: "narrowSymbol",
    }).format(value);
  } catch {
    // 兜底：保证 UI 至少可读
    const symbol = getCurrencySymbolFromCode(
      normalizedCurrency,
      resolvedLocale,
    );
    return `${symbol}${value}`;
  }
}
